import _ from "lodash";
import Owner from "@/model/cospirit-api/Owner";
import PtfFeature from "@/model/cospirit-api/Ptf/PtfFeature";
import {requestStatusNonClientStateMachineByOwner, requestStatusClientStateMachineByOwner} from "@/Constants";
import OwnerRepository from "@/repositories/OwnerRepository";

export default {
    state: {
        owners: [],
        currentOwner: null,
        errorOwner: false,
    },
    mutations: {
        setOwners: (state: any, owners: Owner[]) => {
            _.set(state, "owners", owners);
        },
        setCurrentOwner: (state: { currentOwner: Owner | undefined, owners: Owner[] }, owner: Owner) => {
            if (!state.currentOwner || state.currentOwner.uuid !== owner.uuid) {
                state.currentOwner = owner;
            }

            if (state.currentOwner && owner.logoBase64) {
                _.set(state.currentOwner, "logoBase64", _.get(owner, "logoBase64"));
            }
        },
        setErrorOwners: (state: any, error: boolean) => {
            state.errorOwner = error;
        }
    },
    getters: {
        getCurrentOwner: (state: { currentOwner: Owner|null }): Owner|null => {
            return state.currentOwner;
        },
        getCurrentOwnerUuid: (state: { currentOwner: Owner|null }): string|null => {
            return state.currentOwner && state.currentOwner.uuid ? state.currentOwner.uuid : null;
        },
        getOwners: (state: { owners: Owner[] }): Owner[] => {
            return state.owners;
        },
        hasFeature: (state: { currentOwner: Owner|null }) => (feature: string): boolean => {
            return _.some(_.get(state.currentOwner, "ptfFeatures", []), (line: any) => {
                return line.code === feature;
            });
        },
        hasApplication: (state: { currentOwner: Owner|null }) => (application: string): boolean => {
            return _.some(_.get(state.currentOwner, "applications", []), (line: any) => {
                return line.clientId === application;
            });
        },
        getCurrentFeatures: (state: { currentOwner: Owner|null }): PtfFeature[] => {
            return _.get(state.currentOwner, "ptfFeatures", []);
        },
        getOwnerWorkflow: (state: { currentOwner: Owner | null }) => (isClient: boolean): { [p: string]: string[] } => {
            const stateMachine =
                isClient ? requestStatusClientStateMachineByOwner : requestStatusNonClientStateMachineByOwner;
            const canonicalName = _.get(state.currentOwner, "canonicalName", "");

            return _.get(
                stateMachine,
                canonicalName,
                _.get(stateMachine, "default")
            );
        },
        getErrorOwner: (state: { errorOwner: boolean }): boolean => {
            return state.errorOwner;
        }
    },
    actions: {
        loadOwners: ({ commit, dispatch, state }: any): Promise<void> => {
            const lastOwnerUuid: string = localStorage.getItem("ptf_last_owner_uuid") || "";
            return new Promise((resolve, reject) => {
                if (state.owners.length > 0) {
                    // If owners already loaded, do nothing
                    resolve();
                    return;
                }
                OwnerRepository.get({
                    fields: [
                        "uuid",
                        "fullName",
                        "canonicalName",
                    ],
                    filters: {
                        type: "client",
                        withAncestors: true,
                    },
                }).then((data: object[]) => {
                    const owners = data as Owner[];
                    commit("setOwners", owners);
                    if (!state.currentOwner) {
                        if (0 < owners.length && owners[0].uuid) {
                            let ownerUuid = owners[0].uuid;
                            if (lastOwnerUuid && _.find(owners, (line: any) => line.uuid === lastOwnerUuid)) {
                                ownerUuid = lastOwnerUuid;
                            }
                            dispatch("changeCurrentOwner", ownerUuid).then(() => {
                                resolve();
                            });
                        } else {
                            commit("setErrorOwners", true);
                            reject("No owners found, unauthorized or non-existent user");
                        }
                        return;
                    }

                    resolve();
                }).catch(() => { reject("Error loading owners"); });
            });
        },
        changeCurrentOwner: ({ commit, state }: any, uuid: string): Promise<void> => {
            return new Promise((resolve) => {
                OwnerRepository.get({
                    filters: {
                        uuid,
                        withAncestors: true,
                    },
                    fields: [
                        "uuid",
                        "applications.clientId",
                        "canonicalName",
                        "fullName",
                        "ptfFeatures.uuid",
                        "ptfFeatures.code",
                        "logoBase64",
                        "currentAttributes.uuid",
                        "currentAttributes.value",
                        "currentAttributes.attribute.uuid",
                        "currentAttributes.attribute.label",
                    ],
                }).then((owner: object[]) => {
                    const currentOwner = {
                        ..._.find(state.owners, (line: Owner) => line.uuid === uuid ),
                        ...owner[0]
                    } as Owner;

                    currentOwner.defaultPage = "";
                    if (currentOwner.currentAttributes) {
                        currentOwner.currentAttributes.forEach((attributeValue) => {
                            if ("PTF_INITIAL_PAGE" === attributeValue.attribute.label) {
                                currentOwner.defaultPage = attributeValue.value as string;
                            }
                        });
                    }
                    commit("setCurrentOwner", currentOwner);
                    localStorage.setItem("ptf_last_owner_uuid", uuid);
                    resolve();
                });
            });
        },
    },
};
